import { effect, inject, Injectable, signal } from '@angular/core';
import { take } from 'rxjs';
import { ProfileService } from '@domains/person/profile/data';
import { SelfUserStore } from '@domains/person/user/self/data';

/**
 * Resolves live-participant `PersonId`s to display names through the existing person
 * lookup (`ProfileService.getPersonsLookupByIds` → OData `PersonsList`, one batched
 * call per set of unseen ids). The Q&A/chat payloads (hub and REST) and the ratings carry
 * only `PersonId` — this closes that gap client-side. The producer attendance roster does
 * not go through here: `GET /LiveEvents/{id}/attendances` already returns `PersonName`.
 *
 * The logged-in user (e.g. the live's organizer) never depends on the lookup: their
 * name is seeded from the session (`SelfUserStore.personDataAsync`), since staff may
 * not appear in `PersonsList` at all.
 *
 * Names are progressive enhancement: ids the lookup cannot see (e.g. people from
 * another tenant in a cross-tenant live) simply stay unresolved and the UI keeps the
 * generic "Participante" label. Resolved names are cached for the session and an id
 * is never requested twice.
 */
@Injectable({ providedIn: 'root' })
export class LiveParticipantNamesService {
  private readonly profile = inject(ProfileService);
  private readonly selfUser = inject(SelfUserStore);

  /** Ids already sent to the lookup (resolved or not) — never re-requested. */
  private readonly requested = new Set<string>();

  /** PersonId → display name, filled progressively as lookups land. */
  readonly nameById = signal<Record<string, string>>({});

  constructor() {
    // Seed the session user's own name as soon as the self data is available; their
    // id is marked requested so it never goes out to the lookup.
    effect(() => {
      const self = this.selfUser.personDataAsync();
      if (!self?.PersonId) return;
      const name = self.FirstName || self.DisplayName;
      if (!name) return;
      this.requested.add(self.PersonId);
      if (this.nameById()[self.PersonId] !== name) {
        this.nameById.update((map) => ({ ...map, [self.PersonId]: name }));
      }
    });
  }

  /** Batch-resolve every id not yet requested; no-op when there is nothing new. */
  resolve(personIds: ReadonlyArray<string | null>): void {
    const fresh = [...new Set(personIds.filter((id): id is string => !!id))].filter(
      (id) => !this.requested.has(id),
    );
    if (fresh.length === 0) return;
    for (const id of fresh) this.requested.add(id);

    this.profile
      .getPersonsLookupByIds(fresh)
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          const next = { ...this.nameById() };
          for (const person of res.value) {
            const name = person.FullDisplayName || person.FullName;
            if (name) next[person.PersonId] = name;
          }
          this.nameById.set(next);
        },
        // Silent on failure — rows keep the generic participant label.
        error: () => void 0,
      });
  }
}
